import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Users, IndianRupee, Copy, CheckCircle, Loader2, ArrowRight, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const steps = [
  { num: "01", title: "Generate Your Link", desc: "Enter your email to get a unique referral link instantly." },
  { num: "02", title: "Share With Friends", desc: "Send your link to friends, family, or post on social media." },
  { num: "03", title: "They Sign Up", desc: "When they purchase a hosting plan using your link." },
  { num: "04", title: "You Earn ₹500", desc: "Get ₹500 credit added to your account automatically." },
];

const faqs = [
  { q: "When do I receive my ₹500 credit?", a: "Credits are added within 48 hours after your referred friend makes their first paid purchase." },
  { q: "Is there a limit on how many people I can refer?", a: "No limit! Refer unlimited friends. Each successful referral earns you ₹500 — and your friend gets 10% off their first plan." },
  { q: "What counts as a successful referral?", a: "Your friend must sign up using your referral link and purchase any paid hosting plan (minimum ₹69/month)." },
  { q: "Can I refer myself?", a: "No, self-referrals are not allowed. Referrals must come from unique new customers." },
  { q: "How can I track my referrals?", a: "We'll send you email updates for each successful referral. Detailed tracking dashboard coming soon." },
];

export default function ReferEarn() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const generateLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/generate`, { email });
      setResult(res.data);
    } catch {
      toast.error("Failed to generate referral link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (result?.referral_link) {
      navigator.clipboard.writeText(result.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 3000);
    }
  };

  return (
    <div data-testid="refer-earn-page" className="page-enter pt-24">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(0,255,148,0.07) 0%, transparent 50%), radial-gradient(ellipse at 70% 20%, rgba(0,243,255,0.07) 0%, transparent 50%), #030305" }}>
        <div className="animated-grid absolute inset-0 opacity-20" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
                style={{ background: "rgba(0,255,148,0.1)", border: "1px solid rgba(0,255,148,0.3)", color: "#00FF94" }}>
                <Gift size={12} /> REFER & EARN
              </div>
              <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-4">
                Earn <span style={{ color: "#00FF94" }}>₹500</span> for
                <span className="text-gradient"> Every Friend</span> You Refer
              </h1>
              <p className="font-outfit text-base text-gray-400 mb-6" style={{ fontWeight: 300 }}>
                Share IndiaCloud with your network. When they buy a plan, you earn ₹500 credit —
                and they get 10% off their first purchase. Win-win!
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                {[
                  { label: "Per Referral", value: "₹500", color: "#00FF94" },
                  { label: "Friends Discount", value: "10% OFF", color: "#00F3FF" },
                  { label: "No Limit", value: "Unlimited", color: "#BC13FE" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center px-4 py-2 rounded-xl"
                    style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20` }}>
                    <div className="font-orbitron font-900 text-lg" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="font-outfit text-xs text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Generate form */}
            <div className="glass rounded-2xl p-8"
              style={{ boxShadow: "0 0 30px rgba(0,255,148,0.08)" }}>
              {result ? (
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                    style={{ background: "rgba(0,255,148,0.12)", border: "1px solid rgba(0,255,148,0.3)" }}>
                    <CheckCircle size={28} color="#00FF94" />
                  </div>
                  <h3 className="font-rajdhani font-700 text-xl text-white mb-2">Your Referral Link is Ready!</h3>
                  <p className="font-outfit text-sm text-gray-500 mb-5" style={{ fontWeight: 300 }}>
                    Code: <span className="font-orbitron font-700 text-white">{result.referral_code}</span>
                  </p>
                  <div className="flex items-center gap-2 p-3 rounded-xl mb-5"
                    style={{ background: "rgba(0,243,255,0.05)", border: "1px solid rgba(0,243,255,0.2)" }}>
                    <span className="flex-1 font-outfit text-xs text-gray-400 truncate">{result.referral_link}</span>
                    <button onClick={copyLink} className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg font-rajdhani font-700 text-xs transition-all"
                      style={{ background: copied ? "rgba(0,255,148,0.15)" : "rgba(0,243,255,0.1)", color: copied ? "#00FF94" : "#00F3FF", border: `1px solid ${copied ? "rgba(0,255,148,0.3)" : "rgba(0,243,255,0.25)"}` }}>
                      {copied ? <Check size={11} /> : <Copy size={11} />}
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <p className="font-outfit text-xs text-gray-600">Share this link on WhatsApp, LinkedIn, or email!</p>
                  <button onClick={() => setResult(null)} className="mt-4 font-outfit text-xs text-gray-600 hover:text-gray-400 transition-colors">
                    Generate for a different email
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-5">
                    <Gift size={18} color="#00FF94" />
                    <h3 className="font-rajdhani font-700 text-lg text-white">Get Your Referral Link</h3>
                  </div>
                  <form onSubmit={generateLink} className="flex flex-col gap-4">
                    <div>
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">YOUR EMAIL ADDRESS</label>
                      <input data-testid="refer-email" type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com" className="neon-input" />
                    </div>
                    <button data-testid="generate-referral-btn" type="submit" disabled={loading}
                      className="btn-primary flex items-center justify-center gap-2 py-3 font-700 text-sm"
                      style={{ background: "linear-gradient(135deg, #00FF94, #00F3FF)", color: "#030305" }}>
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
                      {loading ? "GENERATING..." : "GET MY REFERRAL LINK"}
                    </button>
                  </form>
                  <p className="font-outfit text-xs text-gray-600 text-center mt-4">
                    By generating a link you agree to our referral program terms.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-12">
            How It <span className="text-gradient">Works</span>
          </h2>
          <div className="grid md:grid-cols-4 gap-6 relative">
            {steps.map((step, i) => (
              <div key={step.num} className="relative">
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px z-0"
                    style={{ background: "linear-gradient(to right, rgba(0,243,255,0.3), transparent)" }} />
                )}
                <div className="glass rounded-2xl p-5 text-center relative z-10">
                  <div className="font-orbitron font-900 text-2xl text-gradient mb-3">{step.num}</div>
                  <h3 className="font-rajdhani font-700 text-sm text-white mb-2">{step.title}</h3>
                  <p className="font-outfit text-xs text-gray-500" style={{ fontWeight: 300 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-10">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div key={i} className="glass rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,243,255,0.1)" }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-${i}`}>
                  <span className="font-rajdhani font-700 text-sm text-white">{faq.q}</span>
                  <span className="font-orbitron font-900 text-lg ml-4 flex-shrink-0 transition-transform"
                    style={{ color: "#00F3FF", transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="font-outfit text-sm text-gray-400 leading-relaxed" style={{ fontWeight: 300 }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
